import { MarketplaceCartItem } from './types';

/**
 * Pure cart helpers: restore/sanitize persisted carts, validate and clamp
 * quantities against listing constraints, and format quantities/money with
 * Intl.NumberFormat so values render as "1,000 kg" and "700,000 RWF".
 */

export const CART_SCHEMA_VERSION = 3;
export const CART_STORAGE_KEY = 'esanza_marketplace_cart_v3';

// A record with no currency is in the platform's own market, which is Rwanda.
// Every display path reads this one value instead of repeating the code, so a
// change here moves the whole website at once.
export const DEFAULT_CURRENCY = 'RWF';

// Currencies an RFQ or order may be denominated in. Tenants carry their own
// default (default_currency); this is the choice list offered to buyers.
export const SUPPORTED_CURRENCIES = ['RWF', 'USD', 'KES'] as const;

// Units that are sold in fractional amounts (weight/volume). All other units
// (box, crate, bag, piece) are countable and step by whole numbers.
const DECIMAL_STEP_UNITS = new Set(['kg', 'tonne', 'litre']);

const DEFAULT_MIN_QUANTITY = 1;
const DEFAULT_UNIT = 'unit';

// ─── Steps & precision ───────────────────────────────────────────────────────

/**
 * Resolve the quantity step for a unit.
 * Prefers the listing's real quantity_step (from the DB) when provided;
 * otherwise falls back to the platform unit catalogue. The backend enforces
 * the same rule at checkout via the listing's quantity_step.
 */
export function quantityStepForUnit(unit?: string | null, stepOverride?: number | string | null): number {
  if (stepOverride !== undefined && stepOverride !== null && Number.isFinite(Number(stepOverride)) && Number(stepOverride) > 0) {
    return Number(stepOverride);
  }
  const u = (unit || '').trim().toLowerCase();
  return DECIMAL_STEP_UNITS.has(u) ? 0.5 : 1;
}

export function decimalsForStep(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0;
  const asString = String(step);
  const dot = asString.indexOf('.');
  return dot === -1 ? 0 : asString.length - dot - 1;
}

/**
 * Coerce an arbitrary persisted value to a finite number.
 * - Numeric strings are converted (including decimal strings).
 * - Anything that is not finite falls back to `fallback`.
 */
export function toFiniteNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return fallback;
    // Only accept plain decimal notation — never exponent notation, NaN, etc.
    if (/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(trimmed)) {
      return Number(trimmed);
    }
    return fallback;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function alignToStep(value: number, step: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return value;
  const aligned = Math.round(value / step) * step;
  return Number(aligned.toFixed(decimalsForStep(step)));
}

// ─── Constraints & validation ────────────────────────────────────────────────

export interface QuantityConstraints {
  minimumOrderQuantity: number;
  maximumOrderQuantity: number | null;
  availableQuantity: number;
  quantityStep: number;
  unit: string;
}

export function constraintsFromItem(item: Pick<MarketplaceCartItem, 'minimumOrderQuantity' | 'maximumOrderQuantity' | 'availableQuantity' | 'quantityStep' | 'unit'>): QuantityConstraints {
  return {
    minimumOrderQuantity: item.minimumOrderQuantity,
    maximumOrderQuantity: item.maximumOrderQuantity,
    availableQuantity: item.availableQuantity,
    quantityStep: item.quantityStep,
    unit: item.unit,
  };
}

export function constraintsFromListing(listing: {
  minimum_order_quantity?: number | string | null;
  maximum_order_quantity?: number | string | null;
  available_quantity?: number | string | null;
  commercial_unit?: string | null;
  quantity_step?: number | string | null;
}): QuantityConstraints {
  const unit = listing.commercial_unit || DEFAULT_UNIT;
  return {
    minimumOrderQuantity: Math.max(toFiniteNumber(listing.minimum_order_quantity, DEFAULT_MIN_QUANTITY), 0) || DEFAULT_MIN_QUANTITY,
    maximumOrderQuantity: listing.maximum_order_quantity === null || listing.maximum_order_quantity === undefined || listing.maximum_order_quantity === ''
      ? null
      : toFiniteNumber(listing.maximum_order_quantity, 0),
    availableQuantity: Math.max(toFiniteNumber(listing.available_quantity, 0), 0),
    quantityStep: quantityStepForUnit(unit, listing.quantity_step),
    unit,
  };
}

/**
 * Parse a raw user input (from the editable quantity field) into a number.
 * Returns an error string when the input is not an acceptable quantity.
 * Rejects: empty input, non-numeric, exponent notation, NaN/Infinity,
 * negative/zero values, and values with more decimal places than the step
 * allows (excessive precision).
 */
export function parseQuantityInput(raw: string | number, constraints: QuantityConstraints): { value: number; error?: string } {
  const trimmed = typeof raw === 'string' ? raw.trim() : String(raw);
  if (trimmed === '') {
    return { value: NaN, error: 'Enter a quantity' };
  }
  // Plain decimal notation only — rejects "1e3", "Infinity", "NaN", "12abc".
  if (!/^[+]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(trimmed)) {
    return { value: NaN, error: 'Enter a valid number' };
  }
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) {
    return { value: NaN, error: 'Enter a quantity greater than zero' };
  }
  const step = constraints.quantityStep || quantityStepForUnit(constraints.unit);
  const allowedDecimals = decimalsForStep(step);
  const decimals = countDecimals(value);
  if (decimals > allowedDecimals) {
    return {
      value: NaN,
      error: `Enter a quantity in increments of ${formatStep(step)} ${constraints.unit}`,
    };
  }
  return { value };
}

function countDecimals(value: number): number {
  const asString = String(value);
  const dot = asString.indexOf('.');
  return dot === -1 ? 0 : asString.length - dot - 1;
}

/**
 * Validate a numeric quantity against listing constraints.
 * Returns human-readable error strings; empty array means valid.
 */
export function validateQuantity(value: number, constraints: QuantityConstraints): string[] {
  const errors: string[] = [];
  if (!Number.isFinite(value) || value <= 0) {
    return ['Enter a valid positive quantity'];
  }
  const step = constraints.quantityStep || quantityStepForUnit(constraints.unit);
  if (!Number.isInteger(value * 10 ** decimalsForStep(step))) {
    errors.push(`Enter a quantity in increments of ${formatStep(step)} ${constraints.unit}`);
  }
  if (value < constraints.minimumOrderQuantity) {
    errors.push(`Minimum order is ${formatQuantity(constraints.minimumOrderQuantity, constraints.unit, step)}`);
  }
  const maxQty = maxOrderableQuantity(constraints);
  if (constraints.maximumOrderQuantity != null && value > constraints.maximumOrderQuantity) {
    errors.push(`Maximum order is ${formatQuantity(constraints.maximumOrderQuantity, constraints.unit, step)}`);
  } else if (value > constraints.availableQuantity) {
    errors.push(`Only ${formatQuantity(constraints.availableQuantity, constraints.unit, step)} available`);
  }
  void maxQty;
  return errors;
}

function maxOrderableQuantity(constraints: QuantityConstraints): number {
  const caps: number[] = [constraints.availableQuantity];
  if (constraints.maximumOrderQuantity != null) caps.push(constraints.maximumOrderQuantity);
  return Math.min(...caps);
}

/**
 * A listing is orderable only when at least the minimum quantity can be
 * supplied. If available < minimum, never clamp into an invalid order — the
 * item must be treated as unavailable.
 */
export function isOrderable(constraints: QuantityConstraints): boolean {
  return constraints.availableQuantity > 0 && constraints.availableQuantity >= constraints.minimumOrderQuantity;
}

/**
 * Clamp a quantity to [minimum, min(available, maximum)], aligned to the step.
 * Returns 0 when the listing cannot satisfy the minimum order (callers treat
 * 0 as "not orderable / remove"). Never clamps into a value above stock.
 */
export function clampQuantity(value: number, constraints: QuantityConstraints): number {
  if (!isOrderable(constraints)) return 0;
  if (!Number.isFinite(value)) return constraints.minimumOrderQuantity;
  const step = constraints.quantityStep || quantityStepForUnit(constraints.unit);
  const maxQty = maxOrderableQuantity(constraints);
  const clamped = Math.min(Math.max(value, constraints.minimumOrderQuantity), maxQty);
  return alignToStep(clamped, step);
}

export function canIncrement(quantity: number, constraints: QuantityConstraints): boolean {
  return isOrderable(constraints) && quantity < maxOrderableQuantity(constraints);
}

export function canDecrement(quantity: number, constraints: QuantityConstraints): boolean {
  return isOrderable(constraints) && quantity > constraints.minimumOrderQuantity;
}

// ─── Money & subtotal ────────────────────────────────────────────────────────

/**
 * Subtotal is always recalculated from quantity * price; persisted subtotals
 * are never trusted. Rounded to 2 decimal places to avoid float artifacts.
 */
export function computeSubtotal(quantity: number, pricePerUnit: number): number {
  if (!Number.isFinite(quantity) || !Number.isFinite(pricePerUnit)) return 0;
  return Math.round((quantity * pricePerUnit + Number.EPSILON) * 100) / 100;
}

// ─── Formatting ──────────────────────────────────────────────────────────────

export function formatQuantity(quantity: number, unit: string, step?: number): string {
  if (!Number.isFinite(quantity)) return '—';
  const effectiveStep = step && step > 0 ? step : quantityStepForUnit(unit);
  const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: decimalsForStep(effectiveStep),
    minimumFractionDigits: 0,
  });
  return `${formatter.format(quantity)} ${unit}`;
}

/**
 * Single eSanza money formatter: "700,000 RWF". The code always follows the
 * amount so presentation is consistent across the website, supplier portal,
 * checkout, and Super Admin screens regardless of locale currency symbols.
 */
export function formatMoney(amount: number, currency: string): string {
  if (!Number.isFinite(amount)) return '—';
  const code = currency || DEFAULT_CURRENCY;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${formatted} ${code}`;
}

function formatStep(step: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: decimalsForStep(step) }).format(step);
}

// ─── Cart restore / repair ───────────────────────────────────────────────────

/**
 * Rebuild a single persisted cart record into a valid MarketplaceCartItem.
 * Returns null when the record is irreparably corrupt (bad id, non-numeric
 * quantity/price, non-positive quantity). Otherwise repairs it: converts every
 * numeric field to a finite number, clamps the quantity, and recalculates the
 * subtotal from quantity * pricePerUnit.
 */
export function restoreCartItem(raw: any): MarketplaceCartItem | null {
  if (!raw || typeof raw !== 'object') return null;

  const listingId = Number(raw.listingId);
  if (!Number.isInteger(listingId) || listingId <= 0) return null;

  const quantity = toFiniteNumber(raw.quantity, NaN);
  const pricePerUnit = toFiniteNumber(raw.pricePerUnit, NaN);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(pricePerUnit) || pricePerUnit < 0) return null;

  const unit = typeof raw.unit === 'string' && raw.unit.trim() ? raw.unit.trim() : DEFAULT_UNIT;
  const normalizedUnit = typeof raw.normalizedUnit === 'string' && raw.normalizedUnit.trim()
    ? raw.normalizedUnit.trim()
    : unit;
  const currency = typeof raw.currency === 'string' && raw.currency.trim() ? raw.currency.trim() : DEFAULT_CURRENCY;
  const tenantId = toFiniteNumber(raw.tenantId, 0);
  const conversionFactor = Math.max(toFiniteNumber(raw.conversionFactor, 1), 0) || 1;
  const step = quantityStepForUnit(unit);

  const constraints: QuantityConstraints = {
    minimumOrderQuantity: Math.max(toFiniteNumber(raw.minimumOrderQuantity, DEFAULT_MIN_QUANTITY), 0) || DEFAULT_MIN_QUANTITY,
    maximumOrderQuantity: raw.maximumOrderQuantity === null || raw.maximumOrderQuantity === undefined || raw.maximumOrderQuantity === ''
      ? null
      : toFiniteNumber(raw.maximumOrderQuantity, 0),
    availableQuantity: Math.max(toFiniteNumber(raw.availableQuantity, 0), 0),
    quantityStep: step,
    unit,
  };

  // Only clamp when the record actually carries constraint fields (v3+ carts).
  // Legacy records without constraint snapshots keep their quantity — the
  // checkout still validates against live listing limits server-side.
  const hasConstraintSnapshot =
    raw.minimumOrderQuantity !== undefined ||
    raw.availableQuantity !== undefined ||
    raw.maximumOrderQuantity !== undefined;
  if (hasConstraintSnapshot && !isOrderable(constraints)) {
    // Available stock cannot satisfy the minimum order — the record is not
    // orderable, so drop it rather than repairing it into an invalid quantity.
    return null;
  }
  const repairedQuantity = hasConstraintSnapshot ? clampQuantity(quantity, constraints) : quantity;

  return {
    listingId,
    title: typeof raw.title === 'string' && raw.title.trim() ? raw.title.trim() : 'Product',
    catalogName: typeof raw.catalogName === 'string' && raw.catalogName.trim() ? raw.catalogName.trim() : raw.title || 'Product',
    cropType: typeof raw.cropType === 'string' && raw.cropType.trim() ? raw.cropType.trim() : raw.catalogName || raw.title || 'Product',
    quantity: repairedQuantity,
    pricePerUnit,
    unit,
    normalizedUnit,
    conversionFactor,
    currency,
    subtotal: computeSubtotal(repairedQuantity, pricePerUnit),
    supplierName: typeof raw.supplierName === 'string' ? raw.supplierName : '',
    tenantId,
    minimumOrderQuantity: constraints.minimumOrderQuantity,
    maximumOrderQuantity: constraints.maximumOrderQuantity,
    availableQuantity: constraints.availableQuantity,
    quantityStep: constraints.quantityStep,
    imageUrl: typeof raw.imageUrl === 'string' && raw.imageUrl.trim() ? raw.imageUrl.trim() : undefined,
  };
}

/**
 * Parse the persisted cart payload (localStorage) into valid cart items.
 * Handles the versioned shape { version, items } and legacy raw arrays,
 * dropping irreparably corrupt records and repairing the rest.
 */
export function restoreCart(payload: unknown): MarketplaceCartItem[] {
  let rawItems: unknown[] = [];
  if (Array.isArray(payload)) {
    rawItems = payload;
  } else if (payload && typeof payload === 'object' && Array.isArray((payload as any).items)) {
    const storedVersion = Number((payload as any).version);
    // A future schema version we don't understand — start fresh rather than
    // risking invalid orders from a shape we can't parse.
    if (Number.isFinite(storedVersion) && storedVersion > CART_SCHEMA_VERSION) {
      return [];
    }
    rawItems = (payload as any).items;
  }
  const items: MarketplaceCartItem[] = [];
  for (const raw of rawItems) {
    const item = restoreCartItem(raw);
    if (item) items.push(item);
  }
  return items;
}

/** Serialize cart items for localStorage with the schema version stamped on. */
export function serializeCart(items: MarketplaceCartItem[]): string {
  return JSON.stringify({ version: CART_SCHEMA_VERSION, items });
}